import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'

// Our own receipt rather than a link out to Stripe's. Everything here comes
// from the Charge's frozen snapshot: the billed-to name and email as they were
// on the day, not as the account reads now. Printing is print CSS, not a PDF gem.

const money = (cents) => `$${(cents / 100).toFixed(2)}`

const Receipt = () => {
  const { receiptNumber } = useParams()
  const { getRequest } = useQuery()
  const [loading, setLoading] = useState(true)
  const [receipt, setReceipt] = useState(null)

  useEffect(() => {
    getRequest(`/api/v1/billing/receipts/${receiptNumber}`, {}, (err, data) => {
      setLoading(false)
      if (err || !data?.receipt) return
      setReceipt(data.receipt)
    })
  }, [receiptNumber])

  if (loading) return <Loading />

  if (!receipt) {
    return (
      <div className='max-w-2xl mx-auto py-10 text-center'>
        <p className='text-gray-900 dark:text-white font-medium mb-2'>No receipt here.</p>
        <Link to='/settings/billing' className='text-clcpurple hover:underline'>Back to billing history</Link>
      </div>
    )
  }

  const date = new Date(receipt.occurred_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <div className='max-w-2xl mx-auto py-6'>
      <div className='print:hidden flex justify-between items-center mb-4'>
        <Link to='/settings/billing' className='text-sm text-clcpurple hover:underline'>Billing history</Link>
        <button
          type='button'
          onClick={() => window.print()}
          className='text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
        >
          Print
        </button>
      </div>

      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 print:border-0 print:p-0'>
        <div className='flex justify-between items-start mb-8'>
          <div>
            <div className='text-xl font-bold text-gray-900 dark:text-white'>Closing Credits</div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>closingcredits.com</div>
          </div>
          <div className='text-right'>
            <div className='text-sm text-gray-600 dark:text-gray-400'>Receipt</div>
            <div className='font-mono text-sm text-gray-900 dark:text-white'>{receipt.receipt_number}</div>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-6 mb-8 text-sm'>
          <div>
            <div className='text-gray-500 dark:text-gray-400 mb-1'>Billed to</div>
            <div className='text-gray-900 dark:text-white'>{receipt.issued_to.name}</div>
            <div className='text-gray-900 dark:text-white'>{receipt.issued_to.email}</div>
          </div>
          <div>
            <div className='text-gray-500 dark:text-gray-400 mb-1'>Date</div>
            <div className='text-gray-900 dark:text-white'>{date}</div>
            {receipt.payment_method && (
              <>
                <div className='text-gray-500 dark:text-gray-400 mt-3 mb-1'>Paid with</div>
                <div className='text-gray-900 dark:text-white'>{receipt.payment_method}</div>
              </>
            )}
          </div>
        </div>

        <table className='w-full text-sm mb-6'>
          <thead>
            <tr className='border-b border-gray-200 dark:border-gray-700'>
              <th className='text-left py-2 text-gray-500 dark:text-gray-400 font-medium'>Description</th>
              <th className='text-right py-2 text-gray-500 dark:text-gray-400 font-medium'>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className='border-b border-gray-100 dark:border-gray-700'>
              <td className='py-3 text-gray-900 dark:text-white'>{receipt.description}</td>
              <td className='py-3 text-right text-gray-900 dark:text-white'>{money(receipt.amount_cents)}</td>
            </tr>
            {/* A refund is its own line, never a quiet edit of the amount above. */}
            {receipt.refunded_cents > 0 && (
              <tr className='border-b border-gray-100 dark:border-gray-700'>
                <td className='py-3 text-gray-900 dark:text-white'>Refund</td>
                <td className='py-3 text-right text-gray-900 dark:text-white'>−{money(receipt.refunded_cents)}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td className='py-3 font-bold text-gray-900 dark:text-white'>Total</td>
              <td className='py-3 text-right font-bold text-gray-900 dark:text-white'>{money(receipt.net_cents)}</td>
            </tr>
          </tfoot>
        </table>

        <div className='text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4'>
          Processed by Stripe. Questions about this charge? Email support@closingcredits.com.
        </div>
      </div>
    </div>
  )
}

export default Receipt
